import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { FormField } from '@/components/form/FormField';
import { FormInput } from '@/components/form/FormInput';
import { FormTextarea } from '@/components/form/FormTextarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCustomTools, useTools } from '@/hooks/useTools';

import type { AgentDefinitionModalProps } from './types';
import type { Tool } from '@repo/shared-types';

export const AgentDefinitionModal = ({
  open,
  onOpenChange,
  agent,
  onSubmit,
  errors: apiErrors = [],
  isLoading = false,
}: AgentDefinitionModalProps) => {
  const formKey = agent ? `edit-${agent.id}` : `new-${open}`;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content className="bg-background fixed top-1/2 left-1/2 z-50 max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg shadow-lg">
          <AgentDefinitionForm
            key={formKey}
            agent={agent}
            onSubmit={onSubmit}
            apiErrors={apiErrors}
            isLoading={isLoading}
            onClose={() => onOpenChange(false)}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const AgentDefinitionForm = ({
  agent,
  onSubmit,
  apiErrors,
  isLoading,
  onClose,
}: {
  agent: AgentDefinitionModalProps['agent'];
  onSubmit: AgentDefinitionModalProps['onSubmit'];
  apiErrors: NonNullable<AgentDefinitionModalProps['errors']>;
  isLoading: boolean;
  onClose: () => void;
}) => {
  const isEditing = agent !== null;
  const { data: tools = [] } = useTools();
  const { data: customToolsData } = useCustomTools({
    page: 1,
    limit: 100,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const customTools = (customToolsData?.data ?? []).filter((t) => t.is_active);

  const [name, setName] = useState(agent?.name ?? '');
  const [role, setRole] = useState(agent?.role ?? '');
  const [promptTemplate, setPromptTemplate] = useState(agent?.prompt_template ?? '');
  const [welcomeMessage, setWelcomeMessage] = useState(agent?.welcome_message ?? '');
  const [selectedTools, setSelectedTools] = useState<Tool[]>((agent?.tools as Tool[]) ?? []);
  const [selectedCustomToolIds, setSelectedCustomToolIds] = useState<string[]>(
    agent?.custom_tool_ids ?? [],
  );
  const [voiceName, setVoiceName] = useState(agent?.voice_name ?? '');
  const [handoffConditions, setHandoffConditions] = useState(agent?.handoff_conditions ?? '');
  const [isActive, setIsActive] = useState(agent?.is_active ?? true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const getFieldError = (field: string) => {
    const apiError = apiErrors.find((e) => e.field === field);
    return fieldErrors[field] ?? apiError?.message;
  };

  const toggleTool = (toolValue: Tool) => {
    setSelectedTools((prev) =>
      prev.includes(toolValue) ? prev.filter((t) => t !== toolValue) : [...prev, toolValue],
    );
  };

  const toggleCustomTool = (toolId: string) => {
    setSelectedCustomToolIds((prev) =>
      prev.includes(toolId) ? prev.filter((id) => id !== toolId) : [...prev, toolId],
    );
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Name is required';
    else if (!/^[a-z][a-z0-9_]*$/.test(name.trim()))
      errors.name = 'Must be snake_case (e.g., intake_agent)';
    if (!role.trim()) errors.role = 'Role description is required';
    if (!promptTemplate.trim() || promptTemplate.trim().length < 10)
      errors.prompt_template = 'Prompt template must be at least 10 characters';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      name: name.trim(),
      role: role.trim(),
      prompt_template: promptTemplate.trim(),
      welcome_message: welcomeMessage.trim() || undefined,
      tools: selectedTools,
      custom_tool_ids: selectedCustomToolIds,
      voice_name: voiceName.trim() || undefined,
      advanced: {},
      handoff_conditions: handoffConditions.trim() || undefined,
      is_active: isActive,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-center justify-between border-b px-6 py-4">
        <Dialog.Title className="text-lg font-semibold">
          {isEditing ? 'Edit Agent Definition' : 'New Agent Definition'}
        </Dialog.Title>
        <Button type="button" variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-5 px-6 py-5">
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            label="Name"
            htmlFor="agent-name"
            required
            description="Unique snake_case identifier (e.g., intake_agent)"
            error={getFieldError('name')}
          >
            <FormInput
              id="agent-name"
              placeholder="intake_agent"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!getFieldError('name')}
              disabled={isEditing}
            />
          </FormField>

          <FormField
            label="Role"
            htmlFor="agent-role"
            required
            description="Short description of what this agent does"
            error={getFieldError('role')}
          >
            <FormInput
              id="agent-role"
              placeholder="Qualifies the lead and confirms vehicle interest"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              error={!!getFieldError('role')}
            />
          </FormField>
        </div>

        <FormField
          label="Prompt Template"
          htmlFor="agent-prompt"
          required
          error={getFieldError('prompt_template')}
        >
          <FormTextarea
            id="agent-prompt"
            rows={8}
            placeholder="You are an AI assistant for..."
            value={promptTemplate}
            onChange={(e) => setPromptTemplate(e.target.value)}
            error={!!getFieldError('prompt_template')}
          />
        </FormField>

        <FormField
          label="Welcome Message"
          htmlFor="agent-welcome"
          description="Optional opening message spoken at the start of a call (only used if this agent is the entry agent)"
          error={getFieldError('welcome_message')}
        >
          <FormTextarea
            id="agent-welcome"
            rows={2}
            placeholder="Hello, this is..."
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
          />
        </FormField>

        <FormField
          label="Handoff Conditions"
          htmlFor="agent-handoff"
          description="Describe when this agent should transfer the call to another agent"
          error={getFieldError('handoff_conditions')}
        >
          <FormTextarea
            id="agent-handoff"
            rows={3}
            placeholder="Transfer to the booking agent once the caller has confirmed a vehicle..."
            value={handoffConditions}
            onChange={(e) => setHandoffConditions(e.target.value)}
          />
        </FormField>

        <FormField label="Voice Name" htmlFor="agent-voice" description="Optional voice override">
          <FormInput
            id="agent-voice"
            placeholder="Zephyr"
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
          />
        </FormField>

        <div className="space-y-2">
          <p className="text-foreground text-sm font-medium">Built-in Tools</p>
          <p className="text-muted-foreground text-xs">
            Select which built-in tools this agent can use during a call
          </p>
          <div className="flex flex-wrap gap-2">
            {tools.map((tool) => {
              const isSelected = selectedTools.includes(tool.value);
              return (
                <button
                  key={tool.value}
                  type="button"
                  onClick={() => toggleTool(tool.value)}
                  className="focus:ring-primary rounded focus:ring-2 focus:outline-none"
                >
                  <Badge variant={isSelected ? 'default' : 'outline'} className="cursor-pointer">
                    {tool.label}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-foreground text-sm font-medium">Custom Tools</p>
          <p className="text-muted-foreground text-xs">
            Select which custom tools this agent can use during a call. Manage custom tools from the
            Configurations page.
          </p>
          {customTools.length === 0 ? (
            <p className="text-muted-foreground text-xs italic">
              No active custom tools available.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {customTools.map((tool) => {
                const isSelected = selectedCustomToolIds.includes(tool.id);
                return (
                  <button
                    key={tool.id}
                    type="button"
                    onClick={() => toggleCustomTool(tool.id)}
                    className="focus:ring-primary rounded focus:ring-2 focus:outline-none"
                    title={tool.description ?? undefined}
                  >
                    <Badge
                      variant={isSelected ? 'default' : 'outline'}
                      className="cursor-pointer font-mono text-xs"
                    >
                      {tool.name}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <p className="text-sm font-medium">Active</p>
            <p className="text-muted-foreground text-xs">
              Inactive agents are skipped during multi-agent calls
            </p>
          </div>
          <Switch checked={isActive} onCheckedChange={setIsActive} />
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t px-6 py-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Agent'}
        </Button>
      </div>
    </form>
  );
};
